import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import PageHeader from './components/PageHeader';
import DashboardLoader from './components/DashboardLoader';
import { companyAPI } from '../../services/api';
import './ContentOverview.css';
import './CompanyPage.css';

// All known company key-value fields (mirrors backend seed.py simple_keys)
const COMPANY_FIELDS = [
  { key: 'name', label: 'Name', placeholder: 'IWX', required: true },
  { key: 'fullName', label: 'Full Name', placeholder: 'InfiniteWaveX' },
  { key: 'slogan', label: 'Slogan', placeholder: 'Wave the future' },
  { key: 'tagline', label: 'Tagline' },
  { key: 'founded', label: 'Founded (year)' },
  { key: 'founder', label: 'Founder' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website', type: 'url' },
  {
    key: 'featuredProductsLimit',
    label: 'Featured Products Limit',
    type: 'number',
    placeholder: '3',
    min: 1,
    max: 24,
  },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'mission', label: 'Mission', type: 'textarea' },
  { key: 'vision', label: 'Vision', type: 'textarea' },
];

const COMPANY_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h.01"/><path d="M9 12h.01"/><path d="M9 15h.01"/><path d="M9 18h.01"/><path d="M15 9h.01"/><path d="M15 12h.01"/><path d="M15 15h.01"/><path d="M15 18h.01"/>
  </svg>
);

export default function CompanyPage() {
  const navigate = useNavigate();
  const { isEditor } = useAuth();
  const { refresh } = useData();

  const [form, setForm] = useState({});
  const [companyValues, setCompanyValues] = useState([]);
  const [socialLinks, setSocialLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const lastSaved = useRef({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await companyAPI.get();
      const data = res.data || {};
      const init = {};
      COMPANY_FIELDS.forEach((f) => {
        init[f.key] = data[f.key] != null ? String(data[f.key]) : '';
      });
      setForm(init);
      lastSaved.current = { ...init };
      setCompanyValues(Array.isArray(data.values) ? data.values : []);
      setSocialLinks(data.social || {});
    } catch (err) {
      console.error('Failed to load company info:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (key) => {
    if (!isEditor) return;
    const newVal = form[key];
    if (newVal === lastSaved.current[key]) return;
    setSavingKey(key);
    try {
      await companyAPI.patchField(key, 'value', newVal);
      lastSaved.current[key] = newVal;
      refresh();
    } catch (err) {
      alert('Update failed: ' + (err.response?.data?.detail || err.message));
      setForm((prev) => ({ ...prev, [key]: lastSaved.current[key] }));
    } finally {
      setSavingKey(null);
    }
  };

  if (loading) return <DashboardLoader text="Loading company info..." />;

  // Split text fields into two columns
  const textFields = COMPANY_FIELDS.filter((f) => f.type !== 'textarea');
  const longFields = COMPANY_FIELDS.filter((f) => f.type === 'textarea');
  const midpoint = Math.ceil(textFields.length / 2);
  const leftFields = textFields.slice(0, midpoint);
  const rightFields = textFields.slice(midpoint);

  // Split textarea fields into pairs for two-column layout
  const textareaPairs = [];
  for (let i = 0; i < longFields.length; i += 2) {
    textareaPairs.push(longFields.slice(i, i + 2));
  }

  return (
    <div className="company-page">
      <PageHeader
        title="Company Information"
        subtitle="Manage core company details displayed across the site"
        icon={COMPANY_ICON}
        backTo="/dashboard"
      />

      {/* Top row: Core Info (wide) + Related Collections (sidebar) */}
      <div className="company-page__top">
        <div className="co-card company-page__core-card">
          <div className="co-card__header">
            <h3 className="co-card__title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              Core Information
            </h3>
          </div>
          <div className="co-card__body">
            <div className="company-page__fields-grid">
              <div className="company-page__fields-col">
                {leftFields.map((field) => (
                  <div key={field.key} className="co-field">
                    <label className="co-field__label">
                      {field.label}
                      {field.required && <span className="co-field__required">*</span>}
                      {savingKey === field.key && <span className="company-page__saving"> saving…</span>}
                    </label>
                    <div className="co-field__input-wrap">
                      <input
                        className="co-field__input"
                        type={field.type || 'text'}
                        min={field.min}
                        max={field.max}
                        value={form[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        onBlur={() => handleSave(field.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        disabled={!isEditor}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="company-page__fields-col">
                {rightFields.map((field) => (
                  <div key={field.key} className="co-field">
                    <label className="co-field__label">
                      {field.label}
                      {field.required && <span className="co-field__required">*</span>}
                      {savingKey === field.key && <span className="company-page__saving"> saving…</span>}
                    </label>
                    <div className="co-field__input-wrap">
                      <input
                        className="co-field__input"
                        type={field.type || 'text'}
                        min={field.min}
                        max={field.max}
                        value={form[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        onBlur={() => handleSave(field.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        disabled={!isEditor}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Related Collections */}
        <div className="co-card company-page__sidebar-card">
          <div className="co-card__header">
            <h3 className="co-card__title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Related
            </h3>
          </div>
          <div className="co-card__body">
            <div className="company-page__related">
              <button type="button" className="company-page__related-btn" onClick={() => navigate('/dashboard/company-values')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                <span className="company-page__related-label">Company Values</span>
                <span className="company-page__related-count">{companyValues.length}</span>
              </button>
              <button type="button" className="company-page__related-btn" onClick={() => navigate('/dashboard/social-links')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                <span className="company-page__related-label">Social Links</span>
                <span className="company-page__related-count">{Object.keys(socialLinks).length}</span>
              </button>
            </div>

            {/* Quick preview of social links */}
            {Object.keys(socialLinks).length > 0 && (
              <div className="company-page__social-preview">
                <span className="co-field__label" style={{ marginBottom: '0.6rem', display: 'block' }}>Active Platforms</span>
                <div className="company-page__social-chips">
                  {Object.entries(socialLinks).map(([platform, url]) => (
                    <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="company-page__social-chip">
                      {platform}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Quick preview of values */}
            {companyValues.length > 0 && (
              <div className="company-page__values-preview">
                <span className="co-field__label" style={{ marginBottom: '0.6rem', display: 'block' }}>Company Values</span>
                <div className="company-page__values-list">
                  {companyValues.slice(0, 5).map((v, i) => (
                    <div key={v.id || i} className="company-page__value-item">
                      <span className="company-page__value-dot" />
                      <span className="company-page__value-title">{v.title}</span>
                    </div>
                  ))}
                  {companyValues.length > 5 && (
                    <span className="company-page__values-more">+{companyValues.length - 5} more</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Textarea fields in 2-column rows */}
      <div className="company-page__textareas">
        {textareaPairs.map((pair, idx) => (
          <div key={idx} className="company-page__textarea-row">
            {pair.map((field) => (
              <div key={field.key} className="co-card company-page__textarea-card">
                <div className="co-card__header">
                  <h3 className="co-card__title">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/>
                    </svg>
                    {field.label}
                    {savingKey === field.key && <span className="company-page__saving"> saving…</span>}
                  </h3>
                </div>
                <div className="co-card__body">
                  <textarea
                    className="co-field__textarea"
                    value={form[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    onBlur={() => handleSave(field.key)}
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                    disabled={!isEditor}
                    rows={6}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
